import appIndexStyles from './app-index.styles.css?inline';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { WithUserProfile } from './mixins/with-user-profile';
import { createRouter, resolveRouterPath, router } from './router';

import './pages/app-home/app-home';
import './components/header';
import './components/offline-overlay';
import './styles/global.css';
import './global-data/information';

import { setUpdateUserProfileBinder } from './pages/app-login-internal/app-login-internal';
import { SSEClient } from './utility/notifications/sse-client';
import { createDebouncedEvent } from './utility/notifications/debounce-event';
import { NetworkStateNotifier } from './utility/notifications/network-state-notifier';
import { ConnectionHealthMonitor } from './utility/notifications/connection-health';
import {
  endThisTabSessionAs,
  signOutAfterMismatch,
} from './utility/notifications/sign-out-after-mismatch';
import {
  checkServerReachable,
  checkSignIn,
} from './utility/notifications/connection-health-probes';
import { SseLiveUpdateTransport } from './utility/notifications/sse-live-update-transport';
import './components/live-updates-notice';
import {
  showInfoToast,
  showWarningToast,
} from './utility/notifications/easy-toast';
import { UNANSWERED_REQUEST_NOTICE } from './utility/notifications/connection-health-words';
import {
  setServerAnswersSignal,
  setUnansweredRequestListener,
} from './api/unanswered-request';
import { defaultDomain } from './api/default-domain';
import { initializeSseHub } from './utility/notifications/sse-hub';
import { securityNoticeReason } from './utility/notifications/security-notice';
import { startSignedInListeners } from './utility/signed-in-listeners';
import { userDirectoryInstance } from './global-data/user-directory-instance';
import { groupUserIdsCacheInstance } from './global-data/group-user-ids-cache';
import { sessionManager } from './services/session-manager';
import { UserProfile } from './datatypes/user-profile';
import type { SessionEvent } from './datatypes/session-types';
import { pageScrollElementOf } from './utility/page-scroll-element';

@customElement('app-index')
export class AppIndex extends WithUserProfile(LitElement) {
  static styles = unsafeCSS(appIndexStyles);

  get isLoggedIn() {
    return this.userProfile?.isLoggedIn ?? false;
  }

  constructor() {
    super();
  }

  // Removed ResizeObserver logic since header is no longer fixed
  private _headerRO?: ResizeObserver; // retained only to avoid accidental references

  /** Unsubscribe function for session manager events */
  private _sessionUnsubscribe?: () => void;

  /** Stops listening for this person's security notice (see startSignedInListeners). */
  private _securityNoticeUnsubscribe?: () => void;

  /**
   * Decides offline / signed out / live updates reconnecting from the server's answers (D-137).
   * Created with the SSE client in initializeApplicationSettings, started once the tab knows who it
   * is, and the only writer of NetworkStateNotifier's state.
   */
  private _connectionHealth?: ConnectionHealthMonitor;

  private async handleProfileImageUpdated(e: CustomEvent) {
    // SHOW THE BYTES WE ALREADY HAVE, rather than asking the network for what we just sent.
    //
    // The previous version of this handler discarded the event payload and called requestUpdate(),
    // trusting the server's SSE user-updated to refresh the picture. The server side is correct - it
    // invalidates the cache entry and emits the event - and it still cannot work, because the avatar
    // URL is DETERMINISTIC by design (see the system-image caching standard). A re-render therefore
    // sets an `src` identical to the one already there, the browser issues no request at all, and
    // the ETag revalidation the standard relies on never gets its chance. The new image did not
    // appear until the user pressed F5.
    //
    // The event carries the cropped data URL the upload just sent, so the cache takes that and every
    // render site - profile page, header, user-name cards - updates through the subscription it
    // already has.
    const imageBase64 = e.detail?.imageBase64 as string | undefined;
    const userId = this.userProfile?.data?.userId;
    if (imageBase64 && userId) {
      userDirectoryInstance().setLocalProfileImage(String(userId), imageBase64);
    }

    this.requestUpdate();
  }

  private async handleLogout() {
    await this.logout();
  }

  firstUpdated() {
    void this.initializeApp();
  }

  private async initializeApp() {
    // Initialize auth SDKs
    setUpdateUserProfileBinder(this.setUserProfile.bind(this));

    // Initialize SSE client configuration (but don't connect yet)
    await this.initializeApplicationSettings();

    // Verify user login status first
    await this.loadAndVerifyUserProfile();

    // Now connect SSE with the correct mode (user or anonymous) based on login status.
    // AWAITED: this is also where a tab that never signed in is issued its session, and the rest of
    // boot must not run before it has one - anything that takes a lock first would be refused.
    await SSEClient.getInstance().connectAppropriate();

    await this.afterLoginVerified();

    // Connection health starts once the tab knows who it is, so its first sign-in check asks about
    // the person this tab now acts as (D-137).
    this._connectionHealth?.start();

    // Load sessions once after verify - this populates sessionManager.sessions
    // which header and other components can access via the getter
    if (this.isLoggedIn) {
      await sessionManager.loadSessions();
    }

    createRouter();

    // Listen for session switch events to update user profile without page reload
    this._sessionUnsubscribe = sessionManager.subscribe(
      (event) => void this.handleSessionEvent(event)
    );

    router!.addEventListener('route-changed', () => {
      if ('startViewTransition' in document) {
        // Cast to unknown then to the expected function signature to avoid any
        const transition = (
          document as unknown as {
            startViewTransition: (cb: () => void) => {
              ready: Promise<void>;
            };
          }
        ).startViewTransition(() => this.requestUpdate());

        // A view transition is SKIPPED whenever another route change begins before this one starts
        // animating, and the API reports that by rejecting `ready` with "Transition was skipped".
        // That happens on entirely normal flows - signing in redirects to the forced password change
        // and then to home in quick succession - so leaving the rejection unobserved raised an
        // unhandled rejection on a perfectly healthy path.
        //
        // Only `ready` is caught, and only because a skipped transition is expected here. The
        // update callback's own failures reject `updateCallbackDone`, which is deliberately left
        // unobserved so a genuine render failure still surfaces rather than being swallowed
        // alongside the expected interruption.
        void transition.ready.catch(() => undefined);
      } else {
        this.requestUpdate();
      }
      // Settled from updated(), after the render this navigation causes has happened -
      // inside a view transition that render is a frame or more away.
      this.routeSettlePending = true;
    });

    // SSE connection already established above after login verification
    if (!this.isLoggedIn) {
      void router!.navigate(resolveRouterPath('login'));
    }
  }
  /** Set by a navigation; consumed by updated() once that navigation has rendered. */
  private routeSettlePending = false;

  /**
   * Which navigation a settle attempt belongs to. A page that declares a scroll element may not
   * have built it yet, so settling retries across frames; if another navigation starts meanwhile
   * this number moves and the stale chain stops rather than settling the new page's element on
   * the old page's behalf.
   */
  private settleGeneration = 0;

  /**
   * How many frames to wait for a declared scroll element before settling the host instead.
   * Generous, because it costs nothing when the element is already there - the common case
   * resolves on the first attempt - and the alternative is the keyboard silently doing nothing
   * on a page that rendered a frame slower than usual. About a second at 60fps.
   */
  private static readonly SETTLE_FRAMES = 60;

  /** The page title as last announced to assistive technology. */
  @state() private announcedTitle = '';

  protected updated(changed: Map<PropertyKey, unknown>) {
    super.updated(changed);
    if (!this.routeSettlePending) return;
    this.routeSettlePending = false;
    // One frame later, so a page's own first render inside the new route element (and any
    // autofocus it performs) has happened before focus is decided.
    requestAnimationFrame(() => this.settleRouteElement());
  }

  /**
   * After a navigation the page has to (1) start at the top - the router's scrollToTop plugin
   * scrolls the window, which no longer scrolls, and Lit reuses the element across same-route
   * navigations such as tab changes - and (2) hold focus, so PageDown and Space reach it without
   * a click first. Focus is taken only when nothing on the page holds it: a page that focuses its
   * search box on load keeps that. The title is announced through the shell's own live region,
   * the job the router's resetFocus plugin used to do.
   *
   * BOTH LAND ON THE ELEMENT THE PAGE SAYS IT SCROLLS, which is the route element unless the page
   * declares otherwise (`pageScrollElementOf`). D-096 settled the route element because it was the
   * scroll container; #371 later moved the scroller into the tab body on every tabbed page and
   * this did not follow, so on those pages PageDown moved nothing and a tab change kept the last
   * tab's offset. D-131 has the measurements. A page that declares nothing is settled exactly as
   * before.
   */
  private settleRouteElement(
    attempt = 0,
    generation = ++this.settleGeneration
  ) {
    if (generation !== this.settleGeneration) return;
    const route = this.renderRoot.querySelector('main')?.firstElementChild;
    if (!(route instanceof HTMLElement)) return;
    // The route element is reset too, and not only when it IS the scroller: it is still a scroll
    // container by the shell's own `main > *` rule (D-096), so a page that scrolls inside itself
    // AND overflows its host would otherwise keep the host's offset across a tab change.
    route.scrollTop = 0;
    if (attempt === 0) this.announcedTitle = document.title;

    const scroller = pageScrollElementOf(route);
    // WAIT FOR AN ELEMENT A PAGE HAS PROMISED. A tabbed page builds its scroll element inside a
    // custom element whose own shadow root appears a frame or two after the page's first render,
    // so asking once can arrive too early and fall back to the host - which is exactly the state
    // this change exists to fix, and it would fail silently. Measured: the company workspace
    // resolves on the first attempt and the site workspace, reached through a deeper navigation,
    // does not. A page that declares nothing never enters this branch.
    if (
      scroller === route &&
      'pageScrollElement' in route &&
      attempt < AppIndex.SETTLE_FRAMES
    ) {
      requestAnimationFrame(() =>
        this.settleRouteElement(attempt + 1, generation)
      );
      return;
    }

    scroller.tabIndex = -1;
    scroller.scrollTop = 0;
    let active: Element | null = document.activeElement;
    while (active?.shadowRoot?.activeElement) {
      active = active.shadowRoot.activeElement;
    }
    const nothingFocused = !active || active === document.body;
    if (nothingFocused) scroller.focus({ preventScroll: true });
  }

  /** Called after loadAndVerifyUserProfile completes to initialize login-scoped features */
  /**
   * Starts the signed-in listeners (startSignedInListeners): this person's security notice, which
   * the server sends on their own connection when a change alters what they may do (#398 C3).
   * Replaces any notice listener already attached, so a re-run after a reload or a session switch
   * holds one. The company's people list is no longer started here (#398 C5).
   */
  private async afterLoginVerified() {
    if (!this.isLoggedIn) return;
    this.stopListeningForSecurityNotice();
    this._securityNoticeUnsubscribe = startSignedInListeners({
      source: SSEClient.getInstance(),
      onSecurityNotice: (data) => {
        console.log('[app-index] security context invalidated', data);
        void this.handleSecurityContextInvalidated(data);
      },
    });
  }

  /** Stops listening for the security notice (signed out, or switching to another session). */
  private stopListeningForSecurityNotice(): void {
    this._securityNoticeUnsubscribe?.();
    this._securityNoticeUnsubscribe = undefined;
  }

  /**
   * Handle security context invalidation event from server.
   * Shows a toast and refreshes the user profile to get updated security context.
   */
  private async handleSecurityContextInvalidated(
    data: { reason?: string } | unknown
  ): Promise<void> {
    // The reason arrives with term tags, resolved here into the current vocabulary.
    showWarningToast('Security Updated', securityNoticeReason(data), 4000);

    // Reload the user profile to get updated security context
    // This will re-fetch the token which triggers a fresh security context build
    await this.loadAndVerifyUserProfile();

    // Re-initialize login-scoped features with potentially new permissions
    await this.afterLoginVerified();
  }
  async initializeApplicationSettings() {
    // Debounced success notifier (fires once after a quiet period following reconnection attempts)
    const debouncedReconnectToast = createDebouncedEvent({
      title: 'reconnected-toast',
      threshold: 1500, // wait 1.5s to ensure connection is stable
      event: () => {
        showInfoToast('Reconnected to server', '', 2000);
      },
    });

    SSEClient.getInstance(undefined, {
      // The stream no longer decides the page's online state (D-137): the connection-health
      // monitor below does, from the server's own answers. The stream reports only through the
      // adapter behind LiveUpdateTransport.
      onlineCallback: () => {},
      afterOnlineEvent: () => {},
      url: defaultDomain,
    });

    initializeSseHub();

    userDirectoryInstance().listen();
    // Note: Don't connect here - wait until after login verification in firstUpdated()

    const transport = new SseLiveUpdateTransport(SSEClient.getInstance());
    const notifier = NetworkStateNotifier.getInstance();
    let wasOffline = false;
    const publish = () => {
      const monitor = this._connectionHealth;
      if (!monitor) return;
      notifier.setConnectionHealth({
        health: monitor.state,
        liveUpdates: transport.state,
        serverReachable: monitor.serverReachable,
        nextReachabilityCheckAt: monitor.nextReachabilityCheckAt,
      });
      // "Reconnected to server" now means what it says: the server answers again after being
      // unreachable. It used to follow the stream, so every quiet stream blip announced a
      // reconnection to a server that had never gone away.
      if (monitor.state === 'offline') {
        wasOffline = true;
      } else if (wasOffline) {
        wasOffline = false;
        debouncedReconnectToast();
      }
    };
    this._connectionHealth = new ConnectionHealthMonitor({
      transport,
      checkReachable: checkServerReachable,
      checkSignIn,
      onState: publish,
      // A refusal: the server has already ended the sign-in, so the existing local sign-out is all
      // there is (rule 4). A mismatch: the server still holds this tab's session and its locks, so
      // the tab ends them there first, as the person it acts as, then signs out locally (D-143).
      onSignedOut: (reason) => {
        if (reason === 'mismatch') {
          const actingUserId = this.userProfile?.data?.userId;
          void signOutAfterMismatch({
            endTabSession: () => endThisTabSessionAs(actingUserId),
            signOutLocally: () => this.signOutAfterRefusal(),
          });
          return;
        }
        this.signOutAfterRefusal();
      },
      // A single unanswered request, the server answering its check: one short notice, and the
      // page itself shows nothing (rule 8, D-144).
      onRequestUnanswered: () => {
        showWarningToast(UNANSWERED_REQUEST_NOTICE, '');
      },
    });
    // Every request the API client reports as unanswered asks the monitor to check the server at
    // once, so an outage shows as offline without waiting for live updates to drop (@CONN-HEALTH-010).
    setUnansweredRequestListener(() =>
      this._connectionHealth?.checkAfterUnansweredRequest()
    );
    // A first load that got no answer waits on the monitor's own signal and loads again once the
    // server answers (rule 8's first-load clause), rather than each page polling for itself.
    // With no monitor the wait never ends: resolving at once would reload in a tight loop.
    setServerAnswersSignal(
      () =>
        this._connectionHealth?.whenReachable() ??
        new Promise<void>(() => undefined)
    );
    // The overlay shows the live updates' own state beside the server's, which changes without
    // the monitor's decision changing (reconnecting to disconnected, say).
    transport.onState(publish);

    // Removed dynamic header height measurement; header now participates in normal document flow.
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._headerRO?.disconnect();
    this._sessionUnsubscribe?.();
    this.stopListeningForSecurityNotice();
    this._connectionHealth?.stop();
  }

  /**
   * Handle session manager events - updates user profile when session switches
   */
  private async handleSessionEvent(event: SessionEvent): Promise<void> {
    if (event.type === 'session-switched' && event.profileData) {
      // Update the user profile from the switched session data
      const userProfile = new UserProfile(event.profileData);
      await this.setUserProfile(userProfile);

      // Reconnect SSE with new user credentials. Awaited so the fresh session the switch issued
      // is in place before login-scoped features re-subscribe under it.
      await SSEClient.getInstance().connectAppropriate();

      // Re-initialize login-scoped features for new user (includes the security notice listener)
      await this.afterLoginVerified();
    } else if (event.type === 'session-switching') {
      // Stop listening for the old session's security notice before switching
      this.stopListeningForSecurityNotice();

      // Clear group cache when switching away from current session
      groupUserIdsCacheInstance().stopAll();
    }
  }

  render() {
    return html`
      <app-header
        .pageTitle=${router?.context.title ?? ''}
        ?enableBack=${false}
        @logout=${this.handleLogout}
      ></app-header>
      <offline-overlay></offline-overlay>
      <live-updates-notice></live-updates-notice>
      <!-- Announces the page title after each navigation (was the resetFocus plugin's div). -->
      <div class="route-announcer" aria-live="polite">
        ${this.announcedTitle}
      </div>
      <main
        @logout=${this.handleLogout}
        @profile-image-updated=${this.handleProfileImageUpdated}
      >
        ${router?.render()}
      </main>
    `;
  }
}
